import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

/**
 * Live region globale (lineeguida §50): gli annunci per screen reader
 * passano da qui — salvataggi, errori, conferme, cambi di stato.
 *
 * Uso: const { announce } = useAnnouncer(); announce('Pronostici salvati.');
 */
interface AnnouncerContextValue {
  announce: (message: string, politeness?: 'polite' | 'assertive') => void;
}

const AnnouncerContext = createContext<AnnouncerContextValue | null>(null);

export function LiveAnnouncerProvider({ children }: { children: ReactNode }) {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const announce = useCallback((message: string, politeness: 'polite' | 'assertive' = 'polite') => {
    // Svuota ENTRAMBE le regioni e ri-imposta quella giusta: forza l'annuncio
    // anche a testo identico, e soprattutto elimina il residuo dell'altra
    // regione (es. il vecchio "Email o password non corretti" ancora presente
    // dopo un login riuscito).
    setPoliteMessage('');
    setAssertiveMessage('');
    requestAnimationFrame(() => {
      if (politeness === 'assertive') setAssertiveMessage(message);
      else setPoliteMessage(message);
    });
    // Pulizia dopo 7 secondi: evita che navigando si rilegga un vecchio annuncio.
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setPoliteMessage('');
      setAssertiveMessage('');
    }, 7000);
  }, []);

  return (
    <AnnouncerContext.Provider value={{ announce }}>
      {children}
      <div aria-live="polite" aria-atomic="true" className="visually-hidden">
        {politeMessage}
      </div>
      <div aria-live="assertive" aria-atomic="true" role="alert" className="visually-hidden">
        {assertiveMessage}
      </div>
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): AnnouncerContextValue {
  const ctx = useContext(AnnouncerContext);
  if (!ctx) throw new Error('useAnnouncer deve essere usato dentro LiveAnnouncerProvider');
  return ctx;
}
